import Vue from 'vue'
import Router from 'vue-router'
import VueBodyClass from 'vue-body-class'
import { isEmpty, find } from 'lodash'

import store from 'customerPath/store'
import routes, { customerBeforeEach } from 'customerPath/routes'

import { i18n, loadLanguageAsync } from '@/translations/i18n'

Vue.use(Router)

const vueBodyClass = new VueBodyClass(routes)

const createRouter = () =>
  new Router({
    mode: 'history',
    routes: routes,
    linkExactActiveClass: 'active',
    linkActiveClass: 'active-path',
  })

const router = createRouter()

const matchDynamicPage = to => {
  const apiSettings = store.state && store.state.auth && store.state.auth.settings
  let force404 = false
  if (to.name && to.name === 'page') {
    if (to.params.id && to.params.id.indexOf('...pages.') !== -1) {
      const pageKey = to.params.id.split('...pages.')[1]
      if (apiSettings && apiSettings.pages && apiSettings.pages[pageKey]) {
        to.params.id = apiSettings.pages[pageKey]
      } else {
        force404 = true
      }
    }
  }
  return [
    to,
    force404,
  ]
}

router.beforeEach((to, from, next) => {
  // add router based class to body element
  vueBodyClass.guard(to, next)

  let force404
  [
    to,
    force404,
  ] = matchDynamicPage(to)

  // use the language from the params
  let lang = to.params.lang

  const id = to.params.id
  const group_id = to.params.group_id
  const isLanguageChanged = from.params.lang !== lang

  if (!lang) {
    lang = i18n.formatedLocale
  }

  const languages = store.state.translations.languages
  const isLangAvailable = !isEmpty(find(languages, l => l.key === lang))
  const userLang = store.state.auth.selected_language
  const userLangAvailable = userLang && !isEmpty(find(languages, l => l.key === userLang))

  const fallbackLang = userLangAvailable ? userLang : 'de_DE'
  if (!isLangAvailable) {
    return next({ name: 'dashboard', params: { lang: fallbackLang }, query: to.query })
  }

  if (to.path === `/${lang}`) {
    window.scrollTo()
  } else if (to.path === `/${lang}/groups/${id}`) {
    window.scrollTo()
  } else if (to.path === `/${lang}/groups/${group_id}/topics/${id}`) {
    window.scrollTo()
  } else {
    window.scrollTo(0, 0)
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!store.state.auth.status) {
      return next({
        path: `/${lang}/login`,
        query: { redirect: to.path.split('/').slice(2).join('/') },
      })
    }
  } else if (to.matched.some(record => record.meta.requiresAnon)) {
    if (store.state.auth.status) {
      return next({ path: `/${lang}` })
    }
  }

  if (force404) {
    return next({ path: `/${lang}/404` })
  }

  /** @param {array} customerBeforeEach - customer based beforeEach hooks */
  if (customerBeforeEach) {
    customerBeforeEach.forEach(
      /**
       * Loop over customerBeforeEach entries (mapped as shorthand obj).
       * @param {object} obj - a single entry of customerBeforeEach array
       * @returns {function} - next (only if route matches)
       *
       * @example - expected obj structure
       * {
       *   @param {string} obj.toName - <matching route name>
       *   @param {string} obj.toId - <matching route param id>
       *   @param {object} obj.next <target route object>
       * }
       */
      obj => {
        if (obj.toId && obj.toId.indexOf('...pages.') !== -1) {
          const apiSettings = store.state && store.state.auth && store.state.auth.settings
          const pageKey = obj.toId.split('...pages.')[1]
          if (apiSettings && apiSettings.pages && apiSettings.pages[pageKey]) {
            obj.toId = apiSettings.pages[pageKey]
          }
        }

        if (to.name === obj.toName && to.params.id === obj.toId) {
          return next(obj.next)
        }
      },
    )
  }

  loadLanguageAsync(lang, isLanguageChanged).then(_ => next())
})

export default router
